import { Grid3x3, Monitor, Moon, Sun } from "lucide-react";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import { TEXT_SCALE_STEPS, useThemeStore } from "@shared/lib";
import { useUpdateAppSettings } from "@entities/app-settings";
import type { TextScale, Theme } from "@shared/lib";
import type { Lang } from "@shared/api";
import { Setting, SettingHead } from "./Setting";

export const AppearanceSection = () => {
  const { t } = useTranslation("settings");
  const theme = useThemeStore((s) => s.theme);
  const density = useThemeStore((s) => s.density);
  const textScale = useThemeStore((s) => s.textScale);
  const lang = useThemeStore((s) => s.lang);
  const setTheme = useThemeStore((s) => s.setTheme);
  const setDensity = useThemeStore((s) => s.setDensity);
  const setTextScale = useThemeStore((s) => s.setTextScale);
  const setLang = useThemeStore((s) => s.setLang);
  const { mutate: updateSettings } = useUpdateAppSettings();

  const applyTheme = (next: Theme) => {
    setTheme(next);
    updateSettings({ theme: next });
  };

  // Размер текста: ступень применяется сразу (store), на аккаунт уходит рядом.
  const applyTextScale = (next: TextScale) => {
    setTextScale(next);
    updateSettings({ text_scale: next });
  };

  // Local store is the source of truth (instant, offline); also mirror the
  // choice to backend settings so the agent/response layer can read it.
  const applyLang = (next: Lang) => {
    setLang(next);
    updateSettings({ interface_language: next });
  };

  return (
    <>
      <SettingHead
        anchorId="appearance"
        title={t("appearance.title")}
        sub={t("appearance.subtitle")}
      />
      <Setting
        anchorId="appearance-theme"
        label={t("appearance.theme")}
        hint={t("appearance.themeHint")}
      >
        <div className="seg">
          <button
            type="button"
            className={clsx(theme === "system" && "active")}
            onClick={() => {
              applyTheme("system");
            }}
          >
            <Monitor size="0.6875rem" />
            {t("appearance.themeSystem")}
          </button>
          <button
            type="button"
            className={clsx(theme === "dark" && "active")}
            onClick={() => {
              applyTheme("dark");
            }}
          >
            <Moon size="0.6875rem" />
            Dark · Navy ink
          </button>
          <button
            type="button"
            className={clsx(theme === "light" && "active")}
            onClick={() => {
              applyTheme("light");
            }}
          >
            <Sun size="0.6875rem" />
            Light · Paper
          </button>
          <button
            type="button"
            className={clsx(theme === "blueprint" && "active")}
            onClick={() => {
              applyTheme("blueprint");
            }}
          >
            <Grid3x3 size="0.6875rem" />
            Blueprint
          </button>
        </div>
      </Setting>
      <Setting anchorId="appearance-density" label={t("appearance.density")}>
        <div className="seg">
          <button
            type="button"
            className={clsx(density === "comfortable" && "active")}
            onClick={() => {
              setDensity("comfortable");
            }}
          >
            {t("appearance.densityComfortable")}
          </button>
          <button
            type="button"
            className={clsx(density === "compact" && "active")}
            onClick={() => {
              setDensity("compact");
            }}
          >
            {t("appearance.densityCompact")}
          </button>
        </div>
      </Setting>
      <Setting
        anchorId="appearance-text-size"
        label={t("appearance.textSize")}
        hint={t("appearance.textSizeHint")}
      >
        <div
          className="seg"
          role="radiogroup"
          aria-label={t("appearance.textSize")}
          data-testid="text-size-control"
        >
          {TEXT_SCALE_STEPS.map((step) => (
            <button
              key={step}
              type="button"
              role="radio"
              aria-checked={textScale === step}
              data-step={step}
              className={clsx(textScale === step && "active")}
              onClick={() => {
                applyTextScale(step);
              }}
            >
              {step}%
            </button>
          ))}
        </div>
      </Setting>
      <div className="text-size-preview" data-testid="text-size-preview">
        <div className="text-size-preview__title">
          {t("appearance.previewTitle")}
        </div>
        <p className="text-size-preview__body">{t("appearance.previewBody")}</p>
        <div className="text-size-preview__row">
          <button type="button" className="btn primary">
            {t("appearance.previewButton")}
          </button>
          <span className="chip">{t("appearance.previewChip")}</span>
          <span className="hint">{t("appearance.previewHint")}</span>
        </div>
      </div>
      <Setting anchorId="appearance-language" label={t("appearance.language")}>
        <div className="seg">
          <button
            type="button"
            className={clsx(lang === "ru" && "active")}
            onClick={() => {
              applyLang("ru");
            }}
          >
            {t("appearance.languageRu")}
          </button>
          <button
            type="button"
            className={clsx(lang === "en" && "active")}
            onClick={() => {
              applyLang("en");
            }}
          >
            {t("appearance.languageEn")}
          </button>
        </div>
      </Setting>
    </>
  );
};
